import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { WizardStepper } from '@/components/wizards';
import { queryClient } from '@/queryclient';
import { CadenceStep } from '@/sections/settings/components/forms/CadenceStep';
import {
  ScanSchedule,
  type ScanScheduleHandle,
  type ScanScheduleValue,
} from '@/sections/settings/components/forms/ScanSchedule';
import {
  buildPending,
  buildSentence,
  clampDayOfMonth,
} from '@/sections/settings/components/forms/scanScheduleSentence';
import { SentenceText } from '@/sections/settings/components/forms/SentenceText';
import type { CadenceFrequency, ScanCadenceBase, WeekDay } from '@/types';

export interface ScanScheduleWizardProps {
  open: boolean;
  onClose: () => void;
  email?: string;
  /** Initial cadence frequency (from the `cadence` nested in the current `schedule` setting). */
  initialFrequency: CadenceFrequency;
  /** Initial day-of-month (1-31) from the `cadence` nested in the current
   *  `schedule` setting. `31` is the "Last day" sentinel (backend clamps to
   *  each month's last day). */
  initialDayOfMonth: number;
  /** Initial manual-bypass value from the current `schedule` setting. */
  initialManualBypass: boolean;
}

/**
 * Derives the nested cadence payload from Step-1 frequency + the UTC window
 * Step 2 computed. `dayOfWeek` = earliest selected (UTC) weekday — the backend
 * validator enforces it is one of the window days; `dayOfMonth` = Step 2's
 * picker value (`31` is the "Last day" sentinel the backend clamps to each
 * month's true last day). `anchorDate` is never set.
 */
function toCadencePayload(
  frequency: CadenceFrequency,
  utcDays: WeekDay[],
  utcStartHHMM: string,
  dayOfMonth: number
): ScanCadenceBase {
  const earliestDow = utcDays.length
    ? [...utcDays].sort((a, b) => a - b)[0]
    : 0;
  return {
    frequency,
    dayOfWeek: earliestDow,
    dayOfMonth,
    time: utcStartHHMM,
  };
}

export const ScanScheduleWizard: React.FC<ScanScheduleWizardProps> = ({
  open,
  onClose,
  email,
  initialFrequency,
  initialDayOfMonth,
  initialManualBypass,
}) => {
  const [step, setStep] = useState<number>(1);
  const [frequency, setFrequency] =
    useState<CadenceFrequency>(initialFrequency);
  const [dayOfMonth, setDayOfMonth] = useState<number>(
    clampDayOfMonth(initialDayOfMonth)
  );
  const [step2, setStep2] = useState<ScanScheduleValue | undefined>(undefined);
  const [step2Valid, setStep2Valid] = useState(false);
  const [manualBypass, setManualBypass] =
    useState<boolean>(initialManualBypass);
  const [saving, setSaving] = useState(false);

  // Both own-tenant and customer-edit are now 2 steps. The manual-bypass toggle
  // lives inside Step 2 (own-tenant only, gated by ScanSchedule's `!customerEmail`),
  // so there is no longer a dedicated manual step.
  const totalSteps = 2;
  const isLastStep = step === totalSteps;

  const step2Ref = useRef<ScanScheduleHandle>(null);

  // Live summary band (C4): pending sentence (E5) until ≥1 day chosen.
  const hasDays = (step2?.days?.length ?? 0) > 0;
  const pending = buildPending(frequency);
  const sentence =
    hasDays && step2
      ? buildSentence({
          frequency,
          days: step2.days,
          start: step2.start ?? '',
          end: step2.end ?? '',
          dayOfMonth,
        })
      : null;

  async function handleSaveSchedule() {
    if (saving) return;

    // The form is the single tz-conversion source of truth. computeUtc() derives
    // the UTC window WITHOUT writing, so the cadence payload is built from the
    // exact same values the schedule write uses.
    const utc = step2Ref.current?.computeUtc();
    if (!utc) {
      toast.error('Choose at least one day and a valid time window.');
      return;
    }

    setSaving(true);
    try {
      // Window + nested cadence land in one atomic `schedule` PUT — the
      // two-write partial-failure states (cadence saved / window failed) no
      // longer exist by construction.
      const written = await step2Ref.current?.submit(
        toCadencePayload(frequency, utc.days, utc.start, dayOfMonth)
      );
      if (!written) {
        toast.error('Could not save the scan schedule. Please try again.');
        return;
      }

      // belt-and-suspenders: the write invalidates ['my','setting',email]; one
      // explicit invalidation guarantees the card re-reads the document (G5).
      await queryClient.invalidateQueries({ queryKey: ['my', 'setting'] });
      toast.success('Scan schedule updated');
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent
        className="bg-chariot-layer0 max-w-[720px] gap-0 overflow-hidden p-0"
        data-testid="scan-schedule-wizard"
        onOpenAutoFocus={e => {
          // C7: focus the first interactive control of step 1 (the radiogroup).
          e.preventDefault();
          const root = e.currentTarget as HTMLElement;
          root
            .querySelector<HTMLElement>('[role="radio"][aria-checked="true"]')
            ?.focus();
        }}
      >
        {/* C2 — header: title, subtitle, brand-tertiary calendar tile */}
        <div className="border-chariot-stroke flex items-start gap-3 border-b p-4">
          <div className="bg-chariot-brand-tertiary text-chariot-brand-primary flex size-8 shrink-0 items-center justify-center rounded-md">
            <CalendarIcon className="size-4" />
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-base font-bold">
              Edit Scan Schedule
            </DialogTitle>
            <DialogDescription className="text-chariot-text-secondary mt-0.5 text-xs">
              Applied to all assets in this tenant.
            </DialogDescription>
          </div>
        </div>

        {/* C3 — step indicator (canonical WizardStepper, dots variant) */}
        <div className="border-chariot-stroke bg-chariot-layer1 border-b px-4 py-3">
          <WizardStepper
            variant="dots"
            currentStep={step}
            steps={[
              { id: 'cadence', title: 'Cadence' },
              { id: 'days-times', title: 'Days & Times' },
            ]}
            ariaLabel="Scan schedule steps"
          />
        </div>

        {/* C4 — persistent live summary band */}
        <div className="border-chariot-stroke bg-chariot-brand-tertiary border-b px-4 py-3">
          <div className="text-chariot-text-secondary mb-1 text-xs font-bold tracking-wide uppercase">
            This builds the schedule
          </div>
          <p
            className="text-chariot-text-primary text-sm"
            data-testid="scan-schedule-summary-band"
          >
            {sentence ? (
              <SentenceText parts={sentence} />
            ) : (
              <>
                <SentenceText parts={pending.lead} />
                {pending.pending && (
                  <span className="text-chariot-text-disabled font-normal italic">
                    {pending.pending}
                  </span>
                )}
              </>
            )}
          </p>
        </div>

        {/* Step bodies — single fixed height + internal scroll so both steps
            are the same size (no dialog resize between steps). */}
        <div className="h-[480px] min-w-0 overflow-y-auto p-5">
          {step === 1 && (
            <CadenceStep value={frequency} onChange={setFrequency} />
          )}
          {/* Step 2 (Days & Times) is the last step; its Save button calls
              step2Ref.current.submit(). The manual-bypass toggle now renders
              inside ScanSchedule (own-tenant only). */}
          {step === 2 && (
            <div
              role="group"
              aria-label="Step 2: Days and Times"
              data-testid="scan-schedule-wizard-step-2"
            >
              <ScanSchedule
                ref={step2Ref}
                customerEmail={email}
                embedded
                frequency={frequency}
                initialFrequency={initialFrequency}
                manualBypass={manualBypass}
                onManualBypassChange={setManualBypass}
                dayOfMonth={dayOfMonth}
                onDayOfMonthChange={setDayOfMonth}
                onChange={setStep2}
                onValidityChange={setStep2Valid}
              />
            </div>
          )}
        </div>

        {/* C5 — footer chrome (single unified footer) */}
        <div className="border-chariot-stroke bg-chariot-layer0 flex items-center gap-3 border-t p-4">
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="scan-schedule-wizard-cancel"
          >
            Cancel
          </Button>
          <div className="flex-1" />
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(s => Math.max(1, s - 1))}
              data-testid="scan-schedule-wizard-back"
            >
              <ChevronLeftIcon className="size-3.5" />
              Back
            </Button>
          )}
          {!isLastStep && (
            <Button
              variant="default"
              onClick={() => setStep(s => Math.min(totalSteps, s + 1))}
              disabled={step === 2 && !step2Valid}
              data-testid="scan-schedule-wizard-next"
            >
              Next
              <ChevronRightIcon className="size-3.5" />
            </Button>
          )}
          {isLastStep && (
            <Button
              variant="default"
              onClick={handleSaveSchedule}
              disabled={!step2Valid || saving}
              data-testid="scan-schedule-wizard-save"
            >
              Save
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
